import { Button, Form, message } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { updateTroubleshootingGuideAssocations } from '../../../thunks/troubleshooting_guides_thunks';
import { withUserType } from '../../withUserType';
import TroubleshootingAssociationEditFields from './TroubleshootingAssociationEditFields';

const FORM_ID = 'ts-guide-associations-form';

const TroubleshootingAssicationsEditForm: FC<any> = ({
	onCancel,
	onSuccess,
	troubleshootingGuide,
	updateAssociations,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [saving, setSaving] = useState(false);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);
			updateAssociations({
				id: troubleshootingGuide.id,
				...values,
				problemTypeIds: values.allProblemTypes ? [] : values.problemTypeIds,
				locationIds: values.allLocations ? [] : values.locationIds,
				workOrderPriorityIds: values.allWOPriorities ? [] : values.workOrderPriorityIds,
			})
				.then(() => {
					message.success('Associations updated successfully!');
					onSuccess && onSuccess();
				})
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[onSuccess, troubleshootingGuide.id, updateAssociations]
	);

	const noProblemTypesAssociated = useMemo(
		() => nullSafeGetOrElse('problemTypeIds', troubleshootingGuide, []).length === 0,
		[troubleshootingGuide]
	);

	const noLocationsAssociated = useMemo(
		() => nullSafeGetOrElse('locationIds', troubleshootingGuide, []).length === 0,
		[troubleshootingGuide]
	);

	const noWOPrioritiesAssociated = useMemo(
		() => nullSafeGetOrElse('workOrderPriorityIds', troubleshootingGuide, []).length === 0,
		[troubleshootingGuide]
	);

	const initialValues = useMemo(
		() => ({
			...(troubleshootingGuide || {}),
			allProblemTypes: noProblemTypesAssociated,
			allLocations: noLocationsAssociated,
			allWOPriorities: noWOPrioritiesAssociated,
		}),
		[
			noLocationsAssociated,
			noProblemTypesAssociated,
			noWOPrioritiesAssociated,
			troubleshootingGuide,
		]
	);

	return (
		<Form
			layout="horizontal"
			onFinish={handleSubmit}
			initialValues={initialValues}
			id={FORM_ID}
			labelCol={{ span: 8 }}
			wrapperCol={{ span: 16 }}
			labelAlign="left"
			form={form}
		>
			<TroubleshootingAssociationEditFields form={form} />
			<Form.Item>
				<Button type="primary" htmlType="submit" loading={saving}>
					Apply Changes
				</Button>
				<Button onClick={onCancel} style={{ marginLeft: 16 }}>
					Cancel
				</Button>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	fetching: state.troubleshooting_guides.fetching,
	troubleshootingGuide: state.troubleshooting_guides.detail,
	currentUser: state.session.currentUser,
	locations: state.locations,
	problemTypes: state.problem_types,
	workOrderPriorities: state.work_order_priorities,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateAssociations: (entity) =>
		dispatch(updateTroubleshootingGuideAssocations(ownProps.userType)(entity)),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(TroubleshootingAssicationsEditForm as any)
	) as any
);
